/**
 * Templates S (Schermata) and E (Elenco) — the designer's brief part 1 §5 and part 2 §6,
 * `docs/adr/9032`.
 *
 * The fixed anatomy of a page, in this order and no other:
 *
 *   1  the sentence — the conclusion with real numbers, Antonio uppercase
 *      (the page's ONE state strip sits right under it, ADR 9033)
 *   2  at most four KPIs under a 2px ink rule
 *   3  ONE visual, full width — S only; E omits the band and its table is the content
 *   4  ONE table (brief part 1 §1), with its constants band above it when it has one
 *   5  the provenance line
 *
 * Both are layouts. They draw what the caller hands them — a resolved sentence, formatted
 * figures, a chart node, a table node, provenance parts — and fetch nothing. A figure the
 * platform does not publish is a KPI with `arriving` (DEC-96 §4), never a value made up here.
 */

import type { ReactNode } from 'react';

import { VisualCap } from '../layout';

import {
  ConstantsBand, KpiRow, ProvenanceLine, SentenceLine, TemplateStatus,
  type ConstantPair, type TemplateKpi, type TemplateSentence,
} from './parts';

export interface ListTemplateProps {
  /**
   * The conclusion. For an E page, the count and the thing that needs attention («4 piani
   * aspettano la firma, il primo scade il 15/10», part 2 §6). `null` while there is nothing to
   * conclude from — a failed read — when the strip says why.
   */
  readonly sentence: TemplateSentence | null;
  /** At most four. */
  readonly kpis?: readonly TemplateKpi[];
  /** What holds for every row, said once above the table (brief part 1 §1 b). */
  readonly constants?: readonly ConstantPair[];
  /** The one table. */
  readonly table: ReactNode;
  /** Source · date · run, each verbatim. Empty: no line. */
  readonly provenance: readonly string[];
}

export interface ScreenTemplateProps extends ListTemplateProps {
  /** The one visual, full width. Its own title and table view are the chart's (§7.5). */
  readonly visual: ReactNode;
}

/** Template S: sentence · KPIs · visual · table · provenance. */
export function ScreenTemplate(props: ScreenTemplateProps): React.JSX.Element {
  return <Anatomy kind="screen" {...props} />;
}

/** Template E: S without the visual — the table is the content. */
export function ListTemplate(props: ListTemplateProps): React.JSX.Element {
  return <Anatomy kind="list" {...props} visual={null} />;
}

function Anatomy({ kind, sentence, kpis = [], constants = [], visual, table, provenance }: ListTemplateProps & {
  readonly kind: 'screen' | 'list';
  readonly visual: ReactNode;
}): React.JSX.Element {
  // Layouts S and E on the page grid (brief part 3 §B2, ADR 9038): sentence 12 · KPIs 12 · the
  // visual 12 (S) · the table 12, each band one block on the full width. The visual never stands
  // taller than 320px: with more room it widens, it does not grow (`VisualCap`).
  return (
    <div data-template={kind} className="flex flex-col gap-6">
      <TemplateStatus head={sentence === null ? null : <SentenceLine sentence={sentence} />}>
        {kpis.length > 0 && (
          <div data-layout-row="kpis" className="min-w-0">
            <KpiRow kpis={kpis} />
          </div>
        )}
        {kind === 'screen' && (
          <div data-template-visual="" data-layout-row="visual" className="min-w-0">
            <div data-layout-block="" className="min-w-0">
              <VisualCap>{visual}</VisualCap>
            </div>
          </div>
        )}
        <div data-template-table="" data-layout-row="table" className="flex min-w-0 flex-col">
          <div data-layout-block="" className="flex min-w-0 flex-col">
            <ConstantsBand pairs={constants} />
            {table}
          </div>
        </div>
        <ProvenanceLine parts={provenance} />
      </TemplateStatus>
    </div>
  );
}
